import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BLOG_POSTS } from '../data/blogPosts';
import { AUTHORS } from '../data/authors';
import { getProductBySlug, getCategoryBySlug, CATEGORY_SEO } from '../data/products';

const DOMAIN = 'https://dfwfitmeals.com';

const ROUTE_META = {
  '/': {
    title: 'Meal Prep Delivery in Dallas–Fort Worth | Fit Pro Meals',
    description: 'Chef-crafted, macro-tracked meals delivered every Sunday across DFW. Never frozen, from $8/meal. Order by Thursday 1 PM.',
  },
  '/menu': {
    title: "Browse 145+ Macro-Tracked Meals from The Prep Kitchen | DFW Fit Meals",
    description: "Browse The Prep Kitchen's full menu — breakfast, lunch, dinner, bulk protein & sides. Use code NINJA for 20% off your first order.",
  },
  '/how-it-works': {
    title: 'How It Works — Order by Thu, Delivered Sunday | Fit Pro Meals',
    description: 'Pick 8+ meals, order by Thursday 1 PM, and fresh macro-tracked meals arrive Sunday afternoon anywhere in the DFW metroplex. $15 flat delivery.',
  },
  '/delivery': {
    title: 'Meal Prep Delivery Areas — DFW Metroplex | Fit Pro Meals',
    description: 'We deliver fresh meal prep every Sunday to Fort Worth, Dallas, Plano, Frisco, Arlington, Irving, Garland & Grand Prairie. Check your area.',
  },
  '/contact': {
    title: 'Contact Fit Pro Meals — DFW Meal Prep Delivery',
    description: 'Questions about delivery areas, orders or custom meals? Contact Fit Pro Meals — we deliver across the Dallas–Fort Worth metroplex every Sunday.',
  },
  '/blog': {
    title: 'Meal Prep & Nutrition Blog | Fit Pro Meals DFW',
    description: 'Practical guides on meal prep, macros and healthy eating for busy DFW professionals, from the Fit Pro Meals kitchen.',
  },
  '/reviews': {
    title: 'Customer Reviews | Fit Pro Meals DFW',
    description: "See what DFW customers say about Fit Pro Meals' weekly macro-tracked meal delivery.",
  },
  '/rewards': {
    title: 'Rewards Program | Fit Pro Meals',
    description: 'Earn points on every Fit Pro Meals order and redeem them for free meals.',
  },
  '/affiliate': {
    title: 'Affiliate Program | Fit Pro Meals',
    description: 'Earn commission sharing Fit Pro Meals with your DFW audience.',
  },
  '/track': {
    title: 'Track Your Order | Fit Pro Meals',
    description: 'Track the status of your Fit Pro Meals order.',
    noindex: true,
  },
  '/meal-plan': {
    title: 'Weekly Meal Plan Builder — Build & Share Your Plan | DFW Fit Meals',
    description: 'Plan your week with 145+ macro-tracked meals from The Prep Kitchen. Track daily calories, protein, carbs & fat. Share your plan with friends. Code NINJA = 20% off.',
  },
  '/faq': {
    title: 'FAQ — Delivery, Pickup, Prep Pass & More | DFW Fit Meals',
    description: 'Answers to common questions about The Prep Kitchen: ordering, delivery, store pickup, Prep Pass membership, meal freshness, allergens, rewards, and cancellations.',
  },
  '/checkout': {
    title: 'Checkout | Fit Pro Meals',
    description: 'Complete your Fit Pro Meals order.',
    noindex: true,
  },
};

const CITY_META = {
  'fort-worth': { title: 'Meal Prep Delivery in Fort Worth, TX | Fit Pro Meals', description: 'Fresh, chef-crafted meal prep delivered to Fort Worth & Tarrant County every Sunday — Southlake, Keller, Hurst, Euless & more. From $8/meal.' },
  'dallas': { title: 'Meal Prep Delivery in Dallas, TX | Fit Pro Meals', description: 'Fresh, healthy meal prep delivered to Dallas neighborhoods every Sunday. Chef-crafted, macro-tracked, never frozen. From $8/meal.' },
  'plano': { title: 'Meal Prep Delivery in Plano, TX | Fit Pro Meals', description: 'Healthy meal prep delivered to Plano, TX every Sunday. Chef-crafted, macro-tracked meals from $8. Order by Thursday 1 PM.' },
  'frisco': { title: 'Meal Prep Delivery in Frisco, TX | Fit Pro Meals', description: 'Healthy meal prep delivery in Frisco, TX. Fresh meals every Sunday — perfect for active families and athletes. From $8/meal.' },
  'arlington': { title: 'Meal Prep Delivery in Arlington, TX | Fit Pro Meals', description: 'Fresh meal prep delivery in Arlington, TX. Healthy, macro-tracked meals delivered every Sunday. From $8/meal.' },
  'irving': { title: 'Meal Prep Delivery in Irving, TX | Fit Pro Meals', description: 'Healthy meal prep delivery to Irving, TX. Freshly prepared, macro-tracked meals every Sunday. From $8/meal.' },
  'garland': { title: 'Meal Prep Delivery in Garland, TX | Fit Pro Meals', description: 'Meal prep delivery in Garland, TX. Chef-crafted, healthy meals delivered fresh every Sunday. From $8/meal.' },
  'grand-prairie': { title: 'Meal Prep Delivery in Grand Prairie, TX | Fit Pro Meals', description: 'Fresh, healthy meal prep delivered to Grand Prairie, TX every Sunday. Minimum 8 meals, $15 flat delivery.' },
};

function setMetaTag(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function setRobots(noindex) {
  let el = document.querySelector('meta[name="robots"]');
  if (noindex) {
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');
  } else if (el) {
    el.remove();
  }
}

export default function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let meta = ROUTE_META[path];
    let canonicalPath = path === '/' ? '' : path;

    // City pages
    if (!meta && path.startsWith('/delivery/')) {
      const citySlug = path.replace('/delivery/', '');
      meta = CITY_META[citySlug];
    }

    // Category landing pages (/meals/category/:slug)
    if (!meta && path.startsWith('/meals/category/')) {
      const catSlug = path.replace('/meals/category/', '');
      const catSeo = CATEGORY_SEO[catSlug];
      const catName = getCategoryBySlug(catSlug);
      if (catSeo && catName) {
        meta = {
          title: catSeo.title,
          description: catSeo.description,
        };
      }
    }

    // Affiliate product pages (/meals/:slug)
    if (!meta && path.startsWith('/meals/')) {
      const mealSlug = path.replace('/meals/', '');
      const product = getProductBySlug(mealSlug);
      if (product) {
        meta = {
          title: product.affiliateTitle || `${product.name} | DFW Fit Meals`,
          description: product.affiliateMeta || `Review ${product.name} with ${product.protein}g protein. See nutrition details and order from The Prep Kitchen.`,
        };
      }
    }

    // Old /menu/:slug redirect-friendly meta
    if (!meta && path.startsWith('/menu/')) {
      const slug = path.replace('/menu/', '');
      // Will be set by ProductPage itself for dynamic data
      meta = {
        title: `${slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} Meal Prep | Fit Pro Meals`,
        description: `Macro-tracked ${slug.replace(/-/g, ' ')} meal prep. Fresh, never frozen, delivered Sundays in DFW.`,
      };
    }

    // Blog post pages - use article metadata
    if (!meta && path.startsWith('/blog/')) {
      const blogSlug = path.replace('/blog/', '');
      const post = BLOG_POSTS.find(p => p.slug === blogSlug);
      if (post) {
        meta = {
          title: post.meta_title || post.title + ' | Fit Pro Meals',
          description: post.meta_description || post.excerpt,
        };
      } else {
        meta = { title: 'Blog | Fit Pro Meals', description: 'Meal prep tips and guides from Fit Pro Meals.' };
      }
    }

    // Order success
    if (!meta && path.startsWith('/order-success')) {
      meta = { title: 'Order Confirmed | Fit Pro Meals', description: 'Your Fit Pro Meals order has been confirmed.', noindex: true };
    }

    // Author pages
    if (!meta && path.startsWith('/author/')) {
      const authorId = path.replace('/author/', '');
      const author = AUTHORS[authorId];
      if (author) {
        meta = {
          title: `${author.name} — ${author.title} | Fit Pro Meals`,
          description: author.bio,
        };
      }
    }

    // Fallback
    if (!meta) {
      meta = { title: 'Fit Pro Meals | DFW Meal Prep Delivery', description: 'Chef-crafted, macro-tracked meals delivered every Sunday across DFW.' };
    }

    document.title = meta.title;
    setMetaTag('description', meta.description);
    setCanonical(`${DOMAIN}${canonicalPath}`);
    setRobots(meta.noindex);

    // OG tags
    setMetaTag('og:title', meta.title, 'property');
    setMetaTag('og:description', meta.description, 'property');
    setMetaTag('og:url', `${DOMAIN}${canonicalPath}`, 'property');
    setMetaTag('og:type', 'website', 'property');
    setMetaTag('og:site_name', 'Fit Pro Meals', 'property');

    // Twitter tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', meta.title);
    setMetaTag('twitter:description', meta.description);
  }, [location.pathname]);

  return null;
}
